import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail,
  Send,
  Inbox,
  Star,
  Trash2,
  Archive,
  RefreshCw,
  Search,
  Plus,
  Paperclip,
  X,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Reply,
  ReplyAll,
  Forward,
  Tag,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  FileText,
  ExternalLink,
  Settings,
  Filter,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const API = process.env.REACT_APP_BACKEND_URL;

// Email Labels
const LABELS = [
  { id: 'INBOX', name: 'Inbox', icon: Inbox, color: 'text-blue-600' },
  { id: 'STARRED', name: 'Starred', icon: Star, color: 'text-yellow-500' },
  { id: 'SENT', name: 'Sent', icon: Send, color: 'text-green-600' },
  { id: 'DRAFT', name: 'Drafts', icon: FileText, color: 'text-gray-500' },
  { id: 'TRASH', name: 'Trash', icon: Trash2, color: 'text-red-500' },
  { id: 'ARCHIVE', name: 'Archive', icon: Archive, color: 'text-purple-500' },
];

// Email Templates
const EMAIL_TEMPLATES = [
  {
    id: 'interview_invite',
    name: 'Interview Invitation',
    subject: 'Interview Invitation - {{position}} at {{company}}',
    body: `Dear {{candidate_name}},

We are pleased to invite you for an interview for the position of {{position}} at {{company}}.

Interview Details:
- Date: {{date}}
- Time: {{time}}
- Location/Link: {{location}}

Please confirm your availability by replying to this email.

Best regards,
{{sender_name}}
{{company}}`
  },
  {
    id: 'offer_letter',
    name: 'Offer Letter',
    subject: 'Job Offer - {{position}} at {{company}}',
    body: `Dear {{candidate_name}},

We are delighted to extend an offer for the position of {{position}} at {{company}}.

Offer Details:
- Position: {{position}}
- Start Date: {{start_date}}
- Salary: {{salary}}

Please review the attached offer letter and let us know your decision.

Welcome aboard!

Best regards,
{{sender_name}}
{{company}}`
  },
  {
    id: 'rejection',
    name: 'Application Update',
    subject: 'Application Status Update - {{company}}',
    body: `Dear {{candidate_name}},

Thank you for your interest in the {{position}} position at {{company}}.

After careful consideration, we have decided to move forward with other candidates whose qualifications more closely match our current needs.

We appreciate your time and wish you success in your job search.

Best regards,
{{sender_name}}
{{company}}`
  },
  {
    id: 'follow_up',
    name: 'Follow Up',
    subject: 'Following Up - {{position}} Application',
    body: `Dear {{candidate_name}},

I hope this email finds you well. I wanted to follow up regarding your application for the {{position}} position.

{{custom_message}}

Please let me know if you have any questions.

Best regards,
{{sender_name}}
{{company}}`
  }
];

const GmailIntegration = ({ onBack }) => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState('INBOX');
  const [searchQuery, setSearchQuery] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyMode, setReplyMode] = useState(null); // 'reply', 'replyAll', 'forward'
  const [syncing, setSyncing] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEmails, setTotalEmails] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Compose form state
  const [composeTo, setComposeTo] = useState('');
  const [composeCc, setComposeCc] = useState('');
  const [composeBcc, setComposeBcc] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);

  const EMAILS_PER_PAGE = 20;

  useEffect(() => {
    checkConnection();
  }, []);

  useEffect(() => {
    if (connected) {
      fetchEmails();
    }
  }, [connected, selectedLabel, currentPage]);

  const checkConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/gmail/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnected(response.data.connected);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error checking Gmail connection:', error);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/gmail/emails`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          label: selectedLabel,
          page: currentPage,
          limit: EMAILS_PER_PAGE,
          search: searchQuery
        }
      });
      setEmails(response.data.emails || []);
      setTotalEmails(response.data.total || 0);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching emails:', error);
      // Show demo data when not connected
      setEmails(getDemoEmails());
    }
  };

  const getDemoEmails = () => [
    {
      id: 'demo1',
      from: { name: 'John Doe', email: 'john@example.com' },
      to: [{ email: 'you@company.com' }],
      subject: 'Application for Software Engineer Position',
      snippet: 'Dear Hiring Manager, I am writing to express my interest in the Software Engineer position...',
      body: 'Dear Hiring Manager,\n\nI am writing to express my interest in the Software Engineer position at your company. With 5 years of experience in full-stack development...',
      date: new Date().toISOString(),
      unread: true,
      starred: false,
      labels: ['INBOX'],
      attachments: [{ name: 'Resume_JohnDoe.pdf', size: '245 KB' }]
    },
    {
      id: 'demo2',
      from: { name: 'Sarah Smith', email: 'sarah@example.com' },
      to: [{ email: 'you@company.com' }],
      subject: 'Re: Interview Confirmation',
      snippet: 'Thank you for the opportunity. I confirm my attendance for the interview on...',
      body: 'Thank you for the opportunity.\n\nI confirm my attendance for the interview scheduled on Monday at 10 AM.\n\nBest regards,\nSarah',
      date: new Date(Date.now() - 3600000).toISOString(),
      unread: false,
      starred: true,
      labels: ['INBOX', 'STARRED'],
      attachments: []
    },
    {
      id: 'demo3',
      from: { name: 'HR Team', email: 'hr@company.com' },
      to: [{ email: 'team@company.com' }],
      subject: 'Weekly Recruitment Update',
      snippet: 'Here is the weekly recruitment update: 15 new applications received, 5 interviews scheduled...',
      body: 'Team,\n\nHere is the weekly recruitment update:\n\n- 15 new applications received\n- 5 interviews scheduled\n- 2 offers extended\n- 1 candidate joined\n\nRegards,\nHR Team',
      date: new Date(Date.now() - 86400000).toISOString(),
      unread: false,
      starred: false,
      labels: ['INBOX'],
      attachments: []
    }
  ];

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/gmail/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchEmails();
      toast.success('Emails synced successfully');
    } catch (error) {
      toast.error('Failed to sync emails');
    } finally {
      setSyncing(false);
    }
  };

  const handleSendEmail = async () => {
    if (!composeTo || !composeSubject) {
      toast.error('Please fill in recipient and subject');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/gmail/send`, {
        to: composeTo.split(',').map(e => e.trim()),
        cc: composeCc ? composeCc.split(',').map(e => e.trim()) : [],
        bcc: composeBcc ? composeBcc.split(',').map(e => e.trim()) : [],
        subject: composeSubject,
        body: composeBody,
        reply_to: replyMode ? selectedEmail?.id : null,
        attachments: attachments
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Email sent successfully');
      resetCompose();
      setComposeOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const resetCompose = () => {
    setComposeTo('');
    setComposeCc('');
    setComposeBcc('');
    setComposeSubject('');
    setComposeBody('');
    setAttachments([]);
    setReplyMode(null);
    setSelectedTemplate(null);
    setShowCcBcc(false);
  };

  const handleReply = (mode) => {
    if (!selectedEmail) return;
    
    setReplyMode(mode);
    if (mode === 'reply' || mode === 'replyAll') {
      setComposeTo(selectedEmail.from.email);
      if (mode === 'replyAll' && selectedEmail.to) {
        const ccEmails = selectedEmail.to.map(t => t.email).filter(e => e !== 'you@company.com');
        setComposeCc(ccEmails.join(', '));
        setShowCcBcc(ccEmails.length > 0);
      }
      setComposeSubject(`Re: ${selectedEmail.subject}`);
      setComposeBody(`\n\n---\nOn ${new Date(selectedEmail.date).toLocaleString()}, ${selectedEmail.from.name} wrote:\n\n${selectedEmail.body}`);
    } else if (mode === 'forward') {
      setComposeTo('');
      setComposeSubject(`Fwd: ${selectedEmail.subject}`);
      setComposeBody(`\n\n---\nForwarded message:\nFrom: ${selectedEmail.from.name} <${selectedEmail.from.email}>\nDate: ${new Date(selectedEmail.date).toLocaleString()}\nSubject: ${selectedEmail.subject}\n\n${selectedEmail.body}`);
    }
    setComposeOpen(true);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setComposeSubject(template.subject);
    setComposeBody(template.body);
  };

  const handleStarEmail = async (emailId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/gmail/star/${emailId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEmails();
    } catch (error) {
      toast.error('Failed to star email');
    }
  };

  const handleArchiveEmail = async (emailId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/gmail/archive/${emailId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email archived');
      fetchEmails();
      setSelectedEmail(null);
    } catch (error) {
      toast.error('Failed to archive email');
    }
  };

  const handleDeleteEmail = async (emailId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/integrations/gmail/emails/${emailId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email deleted');
      fetchEmails();
      setSelectedEmail(null);
    } catch (error) {
      toast.error('Failed to delete email');
    }
  };

  const handleMarkAsRead = async (emailId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/gmail/read/${emailId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEmails();
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) { // Less than 24 hours
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) { // Less than 7 days
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const handleConnect = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/integrations/gmail/connect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.oauth_url) {
        // Redirect to Google OAuth
        window.location.href = response.data.oauth_url;
      } else if (response.data.connected) {
        toast.success(response.data.message || 'Gmail connected successfully');
        setConnected(true);
      } else {
        toast.info(response.data.message || 'Gmail connection initiated');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Gmail');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to App Hub
        </Button>
        
        <Card className="max-w-lg mx-auto">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Connect Gmail</h2>
            <p className="text-slate-500 mb-6">
              Connect your Gmail account to send and receive emails directly from Officium Connect.
            </p>
            <Button onClick={handleConnect} className="bg-red-500 hover:bg-red-600">
              <Mail className="h-4 w-4 mr-2" />
              Connect with Google
            </Button>
            <p className="text-xs text-slate-400 mt-4">
              We only request access to send and read emails. Your data is secure.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="gmail-integration">
      {/* Header */}
      <div className="p-4 border-b bg-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
            <Mail className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-semibold text-slate-800">Gmail</h1>
            <p className="text-xs text-slate-500">{unreadCount} unread</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={handleSync} disabled={syncing}>
                  <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Sync Emails</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button onClick={() => { resetCompose(); setComposeOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Compose
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Labels */}
        <div className="w-48 border-r bg-slate-50 p-3 hidden md:block">
          <div className="space-y-1">
            {LABELS.map(label => {
              const Icon = label.icon;
              const isActive = selectedLabel === label.id;
              return (
                <button
                  key={label.id}
                  onClick={() => { setSelectedLabel(label.id); setCurrentPage(1); setSelectedEmail(null); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive ? 'bg-white shadow-sm text-slate-800' : 'text-slate-600 hover:bg-white/50'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${label.color}`} />
                  {label.name}
                  {label.id === 'INBOX' && unreadCount > 0 && (
                    <Badge className="ml-auto bg-red-500 text-white text-xs px-1.5">{unreadCount}</Badge>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Email List */}
        <div className={`flex-1 flex flex-col ${selectedEmail ? 'hidden md:flex md:w-1/3' : ''}`}>
          {/* Search */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search emails..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Email List */}
          <div className="flex-1 overflow-y-auto">
            {emails.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Mail className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No emails found</p>
              </div>
            ) : (
              emails.map(email => (
                <div
                  key={email.id}
                  onClick={() => { setSelectedEmail(email); handleMarkAsRead(email.id); }}
                  className={`p-3 border-b cursor-pointer transition-colors ${
                    email.unread ? 'bg-blue-50/50' : 'bg-white'
                  } ${selectedEmail?.id === email.id ? 'bg-blue-100' : 'hover:bg-slate-50'}`}
                >
                  <div className="flex items-start gap-3">
                    <button onClick={(e) => { e.stopPropagation(); handleStarEmail(email.id); }}>
                      <Star className={`h-4 w-4 ${email.starred ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300'}`} />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm truncate ${email.unread ? 'font-semibold' : ''}`}>
                          {email.from?.name || email.from?.email}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">{formatDate(email.date)}</span>
                      </div>
                      <p className={`text-sm truncate ${email.unread ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
                        {email.subject}
                      </p>
                      <p className="text-xs text-slate-500 truncate mt-0.5">{email.snippet}</p>
                      {email.attachments?.length > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Paperclip className="h-3 w-3 text-slate-400" />
                          <span className="text-xs text-slate-400">{email.attachments.length}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalEmails > EMAILS_PER_PAGE && (
            <div className="p-3 border-t flex items-center justify-between text-sm">
              <span className="text-slate-500">
                {(currentPage - 1) * EMAILS_PER_PAGE + 1}-{Math.min(currentPage * EMAILS_PER_PAGE, totalEmails)} of {totalEmails}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={currentPage * EMAILS_PER_PAGE >= totalEmails}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Email Detail */}
        {selectedEmail && (
          <div className="flex-1 flex flex-col bg-white md:border-l">
            {/* Email Header */}
            <div className="p-4 border-b">
              <div className="flex items-center justify-between mb-3">
                <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSelectedEmail(null)}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={() => handleReply('reply')}>
                          <Reply className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Reply</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={() => handleReply('replyAll')}>
                          <ReplyAll className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Reply All</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={() => handleReply('forward')}>
                          <Forward className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Forward</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={() => handleArchiveEmail(selectedEmail.id)}>
                          <Archive className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Archive</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteEmail(selectedEmail.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              <h2 className="text-lg font-semibold text-slate-800 mb-2">{selectedEmail.subject}</h2>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                  {selectedEmail.from?.name?.[0] || selectedEmail.from?.email?.[0] || '?'}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-800">{selectedEmail.from?.name}</p>
                  <p className="text-sm text-slate-500">{selectedEmail.from?.email}</p>
                </div>
                <span className="text-sm text-slate-500">{new Date(selectedEmail.date).toLocaleString()}</span>
              </div>
            </div>

            {/* Email Body */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="prose max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-slate-700">{selectedEmail.body}</pre>
              </div>

              {/* Attachments */}
              {selectedEmail.attachments?.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Attachments ({selectedEmail.attachments.length})</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedEmail.attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
                        <FileText className="h-4 w-4 text-slate-500" />
                        <span className="text-sm">{att.name}</span>
                        <span className="text-xs text-slate-400">{att.size}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={(open) => { if (!open) resetCompose(); setComposeOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {replyMode === 'reply' ? 'Reply' : replyMode === 'replyAll' ? 'Reply All' : replyMode === 'forward' ? 'Forward' : 'New Email'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Templates */}
            {!replyMode && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Use Template</label>
                <Select value={selectedTemplate?.id || ''} onValueChange={(id) => handleTemplateSelect(EMAIL_TEMPLATES.find(t => t.id === id))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMAIL_TEMPLATES.map(template => (
                      <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* To */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">To</label>
              <Input
                placeholder="recipient@email.com"
                value={composeTo}
                onChange={(e) => setComposeTo(e.target.value)}
              />
            </div>

            {/* CC/BCC Toggle */}
            {!showCcBcc && (
              <Button variant="ghost" size="sm" onClick={() => setShowCcBcc(true)}>
                Add Cc/Bcc
              </Button>
            )}

            {showCcBcc && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Cc</label>
                  <Input
                    placeholder="cc@email.com"
                    value={composeCc}
                    onChange={(e) => setComposeCc(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Bcc</label>
                  <Input
                    placeholder="bcc@email.com"
                    value={composeBcc}
                    onChange={(e) => setComposeBcc(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Subject */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Subject</label>
              <Input
                placeholder="Email subject"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
              />
            </div>

            {/* Body */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Message</label>
              <Textarea
                placeholder="Write your message..."
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                rows={10}
                className="resize-none"
              />
            </div>

            {/* Attachments info */}
            <div className="text-xs text-slate-500">
              <AlertCircle className="h-3 w-3 inline mr-1" />
              Attachments will be stored in Google Drive (Zero Storage Policy)
            </div>
          </div>

          <DialogFooter className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => { resetCompose(); setComposeOpen(false); }}>
              Discard
            </Button>
            <Button onClick={handleSendEmail} disabled={sending}>
              {sending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GmailIntegration;
